import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
  Keyboard,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import { axiosinstance } from '../../axiosClient';
import { useResetPasscode } from '../../Context/ResetPasscodeContext';
import Typography from '../../components/Reusables/Typography';
import { shouldUseWebLayout } from '../../utils/layoutUtils';
import { getApiMessage } from '../../utils/apiError';
import { borderWidth, fs, s, v } from '../../utils/libs';
import { GTFonts, theme } from '../../theme';
import { isTablet } from 'react-native-device-info';

const CODE_MIN_LEN = 4;

const ResetPasscodeVerifyCodeScreen = ({ navigation }: { navigation: any }) => {
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const isLargeScreen = windowWidth >= 768;
  const useWebLikeLayout = shouldUseWebLayout(windowWidth, windowHeight);
  const { session, patchSession, clearSession } = useResetPasscode();

  const [code, setCode] = useState('');
  const [counter, setCounter] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    if (!session?.ownerUserId) {
      setErrMsg('Session expired. Returning to login…');
      const t = setTimeout(() => {
        clearSession();
        navigation.navigate('Login');
      }, 2000);
      return () => clearTimeout(t);
    }
  }, [session?.ownerUserId, clearSession, navigation]);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    if (counter > 0) {
      timer = setInterval(() => setCounter(c => c - 1), 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [counter]);

  const handleVerify = async () => {
    setErrMsg('');
    setSuccessMsg('');
    if (!session?.ownerUserId) {
      return;
    }
    if (code.length < CODE_MIN_LEN) {
      setErrMsg(
        `Please enter a valid code (at least ${CODE_MIN_LEN} characters)`,
      );
      return;
    }

    setLoading(true);
    try {
      const verifyPayload = {
        bp_user_id: session.ownerUserId,
        bp_reset_code: `[length=${code.length}]`,
      };
      console.log(
        'bp_owner_reset_code_verification_information',
        'payload',
        verifyPayload,
      );
      const response = await axiosinstance.post(
        'bp_owner_reset_code_verification_information',
        JSON.stringify({
          bp_reset_code: code,
          bp_user_id: session.ownerUserId,
        }),
      );
      console.log(
        'bp_owner_reset_code_verification_information',
        response?.data ?? response,
      );

      if (response?.data?.status === 200) {
        const accessToken = response?.data?.accessToken;
        const refreshToken = response?.data?.refreshToken;
        if (accessToken) {
          patchSession({
            tempAccessToken: String(accessToken),
            tempRefreshToken:
              typeof refreshToken === 'string' ? refreshToken : undefined,
          });
          navigation.navigate('ResetPasscodeNewPasscode');
          return;
        }
      }
      setErrMsg(
        typeof response?.data?.message === 'string'
          ? response.data.message
          : 'Code is incorrect',
      );
    } catch (e: any) {
      console.log(
        'bp_owner_reset_code_verification_information error',
        e?.response?.status,
        e?.response?.data ?? e,
      );
      const status = e?.response?.status;
      const backendMsg = e?.response?.data?.message;
      if (status === 403 && typeof backendMsg === 'string' && backendMsg) {
        setErrMsg(backendMsg);
        return;
      }
      setErrMsg(getApiMessage(e, 'Unable to verify code'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!session?.ownerUserId) return;
    setErrMsg('');
    setSuccessMsg('');
    try {
      const resendPayload = { bp_user_id: session.ownerUserId };
      console.log(
        'bp_owner_resend_reset_code_information',
        'payload',
        resendPayload,
      );
      const resendResponse = await axiosinstance.post(
        'bp_owner_resend_reset_code_information',
        JSON.stringify(resendPayload),
      );
      console.log(
        'bp_owner_resend_reset_code_information',
        resendResponse?.data ?? resendResponse,
      );
      setCounter(60);
      setSuccessMsg('New code sent successfully!');
    } catch (e: any) {
      console.log(
        'bp_owner_resend_reset_code_information error',
        e?.response?.status,
        e?.response?.data ?? e,
      );
      const status = e?.response?.status;
      const backendMsg = e?.response?.data?.message;
      if (status === 403 && typeof backendMsg === 'string' && backendMsg) {
        setErrMsg(backendMsg);
        return;
      }
      setErrMsg(getApiMessage(e, 'Failed to resend code. Please try again.'));
    }
  };

  const goBackToLogin = useCallback(() => {
    clearSession();
    navigation.navigate('Login');
  }, [clearSession, navigation]);

  const formBody = (
    <>
      <Typography style={styles.subtitle}>
        We sent a verification code to your mobile number.
      </Typography>
      {errMsg ? (
        <View style={styles.errorContainer}>
          <Typography style={styles.errorText}>{errMsg}</Typography>
        </View>
      ) : null}
      {successMsg ? (
        <View style={styles.successContainer}>
          <Typography style={styles.successText}>{successMsg}</Typography>
        </View>
      ) : null}
      <Typography style={styles.label}>Verification code</Typography>
      <TextInput
        style={styles.input}
        placeholder="Enter code"
        placeholderTextColor={theme.colors.textTertiary}
        keyboardType="number-pad"
        value={code}
        onChangeText={setCode}
        autoFocus
        onKeyPress={(e: any) => {
          const key = e?.nativeEvent?.key ?? e?.key;
          if (key !== 'Enter') return;
          e?.preventDefault?.();
          e?.stopPropagation?.();
          if (loading || code.length < CODE_MIN_LEN) return;
          void handleVerify();
        }}
        returnKeyType="done"
        onSubmitEditing={() => {
          if (loading || code.length < CODE_MIN_LEN) return;
          void handleVerify();
        }}
      />
      <TouchableOpacity
        style={[
          styles.primaryButton,
          (code.length < CODE_MIN_LEN || loading) &&
            styles.primaryButtonDisabled,
        ]}
        disabled={code.length < CODE_MIN_LEN || loading}
        onPress={() => void handleVerify()}>
        {loading ? (
          <ActivityIndicator color={theme.colors.textInverse} />
        ) : (
          <Typography style={styles.primaryButtonText}>Verify</Typography>
        )}
      </TouchableOpacity>
      <View style={styles.resendContainer}>
        {counter > 0 ? (
          <Typography style={styles.timerText}>
            Resend code in {counter} seconds
          </Typography>
        ) : (
          <TouchableOpacity onPress={() => void handleResend()}>
            <Typography style={styles.resendText}>
              Didn't receive code?{'\u00A0'}
              <Typography style={styles.resendLink}>Resend now</Typography>
            </Typography>
          </TouchableOpacity>
        )}
      </View>
      <TouchableOpacity onPress={goBackToLogin} style={styles.linkRow}>
        <Typography style={styles.linkText}>Back to login</Typography>
      </TouchableOpacity>
    </>
  );

  if (useWebLikeLayout && isLargeScreen) {
    return (
      <View style={styles.webOuter}>
        <ScrollView
          style={styles.webScroll}
          contentContainerStyle={styles.webScrollContent}
          keyboardShouldPersistTaps="handled">
          <View style={styles.webFormCard}>
            {/* <Typography style={styles.title}>Verify code</Typography> */}
            {formBody}
          </View>
        </ScrollView>
      </View>
    );
  }

  return (
    <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
      <View style={styles.container}>
        {/* <Typography style={styles.title}>Verify code</Typography> */}
        {formBody}
      </View>
    </TouchableWithoutFeedback>
  );
};

export default ResetPasscodeVerifyCodeScreen;

const styles = StyleSheet.create({
  webOuter: {
    flex: 1,
    backgroundColor: theme.colors.backgroundGray50,
    minHeight: '100%',
  },
  webScroll: { flex: 1 },
  webScrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: s(24),
    paddingVertical: v(48),
  },
  webFormCard: {
    backgroundColor: theme.colors.surface,
    borderRadius: s(24),
    padding: s(40),
    width: '100%',
    maxWidth: s(480),
    borderWidth: borderWidth(1),
    borderColor: theme.colors.borderGray200,
  },
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
    justifyContent: 'center',
    padding: s(24),
    paddingBottom: v(120),
  },
  title: {
    fontSize: fs(22),
    lineHeight: fs(26),
    fontFamily: GTFonts.semibold,
    color: theme.colors.primary,
    marginBottom: v(8),
  },
  subtitle: {
    fontSize: isTablet() ? 18 : 14,
    color: theme.colors.textSecondary,
    lineHeight: 22,
    marginBottom: v(16),
  },
  label: {
    fontSize: fs(14),
    lineHeight: fs(20),
    fontFamily: GTFonts.semibold,
    color: theme.colors.textGray700,
    marginBottom: v(8),
  },
  errorContainer: {
    backgroundColor: theme.colors.errorLight,
    borderRadius: s(8),
    padding: s(12),
    marginBottom: v(16),
  },
  errorText: {
    color: theme.colors.errorDark,
    textAlign: 'center',
    fontSize: fs(14),
    lineHeight: fs(20),
  },
  successContainer: {
    backgroundColor: theme.colors.successLight,
    borderRadius: s(8),
    padding: s(12),
    marginBottom: v(16),
  },
  successText: {
    color: theme.colors.successDark,
    textAlign: 'center',
    fontSize: fs(14),
    lineHeight: fs(20),
  },
  input: {
    backgroundColor: theme.colors.surface,
    borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
    borderColor: theme.colors.primary,
    borderRadius: s(8),
    padding: s(16),
    fontSize: fs(18),
    lineHeight: fs(22),
    textAlign: 'center',
    letterSpacing: 4,
    marginBottom: v(16),
  },
  primaryButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: s(8),
    paddingVertical: v(14),
    alignItems: 'center',
  },
  primaryButtonDisabled: {
    backgroundColor: theme.colors.primaryLight,
  },
  primaryButtonText: {
    color: theme.colors.textInverse,
    fontSize: isTablet() ? 18 : 16,
    lineHeight: fs(isTablet() ? 22 : 20),
    fontWeight: '600',
    fontFamily: GTFonts.semibold,
  },
  resendContainer: {
    marginTop: v(20),
    alignItems: 'center',
  },
  timerText: {
    color: theme.colors.textSecondary,
    fontSize: fs(14),
  },
  resendText: {
    color: theme.colors.textSecondary,
    fontSize: fs(14),
  },
  resendLink: {
    color: theme.colors.primary,
    fontWeight: '600',
    textDecorationLine: 'underline',
    fontFamily: GTFonts.semibold,
  },
  linkRow: {
    alignItems: 'center',
    marginTop: v(20),
  },
  linkText: {
    color: theme.colors.primary,
    fontSize: fs(14),
    lineHeight: fs(16),
    fontFamily: GTFonts.semibold,
    textDecorationLine: 'underline',
  },
});
